<template>
  <div
    :class="[
      'calendar-input transition-input',
      value && 'filled',
      error && 'error',
      disabled && 'disabled',
      alignRight && 'position-right',
    ]"
  >
    <label class="d-block">{{ label }}</label>
    <div class="input-wrapper">
      <Datepicker
        ref="datePicker"
        :placeholder="placeholder"
        :value="value"
        monday-first
        :disabled="disabled"
        calendar-class="calendar"
        :disabledDates="disabledDates"
        full-month-name
        :language="translations"
        :open-date="openDate"
        @selected="$emit('update:value', $event)"
        @closed="$emit('blur')"
      />
      <FontIcon name="ui_calendar" class="calendar-icon" />
    </div>

    <InputError :error="error" />
  </div>
</template>

<style lang="scss">
.calendar-input {
  position: relative;
  text-align: left;

  .input-wrapper {
    position: relative;
  }

  &.transition-input input {
    padding-right: rem(30px);
  }

  .calendar-icon {
    position: absolute;
    right: rem(14px);
    top: 50%;
    transform: translateY(-50%);
    @include setColor(--color-icon1);
    font-size: rem(20px);
    pointer-events: none;
  }

  .calendar {
    margin-top: rem(7px);
    @include setColor(--color-text);
    box-shadow: 2px 2px 5px 0px rgba(0, 0, 0, 0.3);

    header {
      span {
        width: 100% !important;
        font-size: rem(18px);
        @include setColor(--color-text-negative);
        @include setColor(--color-selection, background-color);
        text-transform: none !important;

        &.day__month_btn {
          text-transform: uppercase;
          font-size: rem(14px);
        }
      }

      .prev,
      .next {
        width: rem(30px) !important;
        height: rem(30px) !important;
        position: absolute !important;
        text-indent: 0 !important;

        &.disabled {
          opacity: 0.5;
        }

        &:hover:not(.blank) {
          @include setColor(--color-selection, background-color);
        }

        &:after {
          border: none !important;
          margin: 0 !important;
        }

        svg {
          position: absolute;
          width: rem(14px);
          height: rem(14px);
          top: 50%;
          left: 50%;
          margin-left: rem(-7px);
          margin-top: rem(-7px);
        }

        path {
          @include setColor(--color-cta-negative, fill);
        }
      }

      .prev {
        left: 0;
      }

      .next {
        right: 0;

        svg {
          transform: rotate(180deg);
        }
      }
    }

    .cell {
      border: none !important;
      font-size: rem(12px);

      &.day:hover:not(.blank),
      &.highlighted {
        @include setColor(--color-text-negative);
        @include setColor(--color-selection, background-color);
      }

      &.day-header {
        font-weight: 700;
        @include setColor(--color-text);
      }

      &.day {
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        &.selected {
          @include setColor(--color-selection, background-color);
          @include setColor(--color-text-negative);

          &:hover {
            @include setColor(--color-text-negative);
          }
        }
      }
    }
  }

  &.position-right .calendar {
    right: 0;
  }
}
</style>

<script>
import Datepicker from "vuejs-datepicker";

import InputError from "../InputError/InputError";
import FontIcon from "../../FontIcon";

import * as translations from "vuejs-datepicker/dist/locale";

export default {
  components: {
    Datepicker,
    FontIcon,
    InputError,
  },

  props: {
    value: {
      type: Date,
      required: false,
    },
    excludeDatesTo: {
      type: Date,
      default: null,
    },
    excludeDatesFrom: {
      type: Date,
      required: false,
    },
    minDate: {
      type: Date,
      default: () => new Date(),
    },
    maxDate: {
      type: Date,
      required: false,
    },
    error: {
      type: String,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
    },
    label: {
      type: String,
      required: true,
    },
    locale: {
      type: String,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    alignRight: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    translations() {
      return translations[this.locale];
    },

    openDate() {
      if (this.value) return this.value;

      if (this.minDate || this.excludeDatesTo) {
        const tsp1 = this.minDate ? this.minDate.getTime() : 0;
        const tsp2 = this.excludeDatesTo
          ? this.excludeDatesTo.getTime() + 24 * 60 * 60 * 1000
          : 0;

        return new Date(Math.max(tsp1, tsp2));
      }

      return null;
    },

    disabledDates() {
      const excludeFrom = this.excludeDatesFrom
        ? this.excludeDatesFrom
        : this.maxDate;
      const excludeTo = this.excludeDatesTo
        ? this.excludeDatesTo
        : this.minDate;

      const disabledDates = {};

      if (excludeTo) {
        disabledDates.to = new Date(
          excludeTo.getFullYear(),
          excludeTo.getMonth(),
          excludeTo.getDate(),
        );
      }
      if (excludeFrom) {
        disabledDates.from = new Date(
          excludeFrom.getFullYear(),
          excludeFrom.getMonth(),
          excludeFrom.getDate(),
        );
      }

      // Handle excluded dates
      disabledDates.dates = [
        this.excludeDatesTo && disabledDates.to,
        this.excludeDatesFrom && disabledDates.from,
      ].filter(Boolean);

      return disabledDates;
    },
  },
};
</script>
